/**
 * Accessible status icon + color + label mapping.
 * WCAG 2.2 Level AA: information must not be conveyed by color alone.
 * Every status uses shape (icon) + color + text for full accessibility.
 */

export type StatusKey =
  | 'excellent' | 'optimal' | 'inRange' | 'good' | 'stable'
  | 'fair' | 'borderline' | 'moderate' | 'mildShift'
  | 'needsAttention' | 'outOfRange' | 'high' | 'significantShift'
  | 'unknown';

export interface StatusDef {
  icon: string;
  iconClass: string;
  label: string;
}

/** Map a status key to an icon + color class + default label for accessible display. */
export const STATUS_ICONS: Record<StatusKey, StatusDef> = {
  // Positive tier
  excellent:        { icon: '✓', iconClass: 'text-emerald-600 dark:text-emerald-400', label: 'Excellent' },
  optimal:          { icon: '✓', iconClass: 'text-emerald-600 dark:text-emerald-400', label: 'Optimal' },
  inRange:          { icon: '✓', iconClass: 'text-green-600 dark:text-green-400',     label: 'In range' },
  good:             { icon: '✓', iconClass: 'text-green-600 dark:text-green-400',     label: 'Good' },
  stable:           { icon: '✓', iconClass: 'text-green-600 dark:text-green-400',     label: 'Stable' },
  // Caution tier
  fair:             { icon: '⚠', iconClass: 'text-amber-500 dark:text-amber-400',     label: 'Fair' },
  borderline:       { icon: '⚠', iconClass: 'text-amber-500 dark:text-amber-400',     label: 'Borderline' },
  moderate:         { icon: '⚠', iconClass: 'text-amber-500 dark:text-amber-400',     label: 'Moderate' },
  mildShift:        { icon: '⚠', iconClass: 'text-amber-500 dark:text-amber-400',     label: 'Mild shift' },
  // Alert tier
  needsAttention:   { icon: '✕', iconClass: 'text-red-600 dark:text-red-400',         label: 'Needs Attention' },
  outOfRange:       { icon: '✕', iconClass: 'text-red-600 dark:text-red-400',         label: 'Out of range' },
  high:             { icon: '✕', iconClass: 'text-red-600 dark:text-red-400',         label: 'High' },
  significantShift: { icon: '✕', iconClass: 'text-red-600 dark:text-red-400',         label: 'Significant shift' },
  // Neutral
  unknown:          { icon: '?', iconClass: 'text-gray-500 dark:text-gray-400',       label: 'Unknown' },
};

/**
 * Map a pillar score label (from pillarScoring.ts ranges) to its status icon.
 * Returns the icon character for the given label, or empty string if not mapped.
 */
export function iconForPillarLabel(label: string): string {
  const l = label.toLowerCase();
  if (l.includes('excellent') || l.includes('optimal') || l.includes('minimal') || l.includes('resilient')) return '✓';
  if (l.includes('good') || l.includes('low stress') || l.includes('low risk')) return '✓';
  if (l.includes('fair') || l.includes('moderate') || l.includes('elevated')) return '⚠';
  if (l.includes('needs attention') || l.includes('high')) return '✕';
  if (l.includes('not enough data') || l.includes('unknown') || l.includes('no data')) return '?';
  return '';
}
